import * as config from '@terraframing-suite/rx-config';
import { Box, Divider, Grid, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { isNumber } from 'lodash-es';
import { NumericTextField } from '../../../../components/NumericTextField';

export function LimeCalculator(props) {
  const { state, passThrough, onChange = () => {} } = props;
  const stateConfig = config.lime.states[state];

  const [variables, setVariables] = useState({
    cce: '',
    magnesium: '',
    calcium: '',
  });

  useEffect(() => {
    if (!stateConfig?.sieves) return;

    setVariables((current) => ({
      cce: current.cce,
      magnesium: current.magnesium,
      calcium: current.calcium,
      ...Object.fromEntries(
        stateConfig.sieves.map((size) => [`ms${size}`, passThrough || '']),
      ),
    }));
  }, [passThrough, stateConfig]);

  function updateVariables({ target }) {
    setVariables((current) => ({
      ...current,
      [target.name]: target.error ? `${target.value}` : target.value,
    }));
  }

  const calculated = useMemo(() => {
    const canCalculate = Object.values(variables).every((v) => isNumber(v));
    return canCalculate && stateConfig.calculateQuality
      ? Math.round(stateConfig.calculateQuality(variables))
      : '';
  }, [stateConfig, variables]);

  useEffect(() => {
    onChange({ quality: calculated, variables });
  }, [calculated, onChange, variables]);

  if (!stateConfig?.sieves?.length)
    return (
      <Grid item xs={12}>
        <Typography color="error">{`Calculator not available for ${stateConfig?.label || state}`}</Typography>
      </Grid>
    );

  return (
    <>
      <Grid item xs={6}>
        <NumericTextField
          required
          min={40}
          max={120}
          ignoreNonNumericInput
          size="small"
          InputLabelProps={{ shrink: true }}
          fullWidth
          label="CCE"
          name="cce"
          value={variables.cce}
          onChange={updateVariables}
        />
      </Grid>
      <Grid item xs={6}>
        <NumericTextField
          required
          min={1}
          max={100}
          ignoreNonNumericInput
          size="small"
          InputLabelProps={{ shrink: true }}
          fullWidth
          label="Calcium Percentage"
          name="calcium"
          value={variables.calcium}
          onChange={updateVariables}
        />
      </Grid>
      <Grid item xs={6}>
        <NumericTextField
          required
          min={1}
          max={100}
          ignoreNonNumericInput
          size="small"
          InputLabelProps={{ shrink: true }}
          fullWidth
          label="Mag Percentage"
          name="magnesium"
          value={variables.magnesium}
          onChange={updateVariables}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {stateConfig.sieves.map((sieve) => (
        <Grid item xs={12} sm={6} key={sieve}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              color="text"
              sx={{ width: 80 }}
              defaultValue={sieve}
              label="Sieve Size"
              InputProps={{ readOnly: true }}
            />
            <NumericTextField
              sx={{ flex: 1 }}
              disabled={Boolean(passThrough)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              required
              ignoreNonNumericInput
              min={1}
              max={100}
              label="Enter Percentage"
              name={`ms${sieve}`}
              value={variables[`ms${sieve}`] || ''}
              data-testid={`sieve-${sieve}`}
              onChange={updateVariables}
            />
          </Box>
        </Grid>
      ))}
    </>
  );
}
